import NoData from '@/components/common/NoData'
import ButtonField from '@/components/form-control/ButtonField'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CharacterPlusIcon,
} from '@/components/icons'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import useSearchFlowValve from '@/features/config/hooks/hydraulic/flowValve/useSearchFlowValve'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { Collapse, IconButton, Stack, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import FlowValveTable from './FlowValveTable'
import classes from './style.module.scss'

interface ValveProps {
  flowRuleId: number
}

const FlowValveView = (props: ValveProps) => {
  const { t } = useTranslation()
  
  const { flowRuleId } = props
  const { interfaceId } = useAuthContext()
  const { data, refetch } = useSearchFlowValve(Number(interfaceId))
  const [open, setOpen] = useState<boolean>(false)
  const { openSidebar, setSidebarView } = useConfigUI()
  const { openModal, setModalView } = useConfigUI()
  const { isViewOnly } = usePermission()


  const valveList = useMemo(
    () => data?.list.filter((item) => item.flowRuleId === flowRuleId),
    [flowRuleId, data],
  )

  const handleAddValve = () => {
    if (isViewOnly) return
    openSidebar()
    setSidebarView({ type: 'FLOW_VALVE_VIEW', flowRuleId: flowRuleId })
  }

  const handleDeleteItem = (id: string, interfaceId: string) => {
    if (isViewOnly) return
    openModal()
    setModalView({
      type: 'DELETE_FLOW_VALVE_VIEW',
      id: id,
      interfaceId: interfaceId,
      onSuccess: async () => {
        await refetch()
      },
    })
  }

  useEffect(() => {
    if (flowRuleId && valveList) {
      setOpen(true)
    }
    //eslint-disable-next-line
  }, [valveList])

  return (
    <Stack className={classes.valveContainer}>
      <Stack className={classes.valveHead}>
        <Stack className={classes.leftHeader}>
          <Typography className={classes.titleValve}>{t('flowValve')}</Typography>
          <IconButton
            aria-label="expand row"
            sx={{ padding: 0 }}
            onClick={() => setOpen(!open)}
          >
            {open ? (
              <ArrowUpIcon sx={{ color: 'text.main' }} />
            ) : (
              <ArrowDownIcon sx={{ color: 'text.main' }} />
            )}
          </IconButton>
        </Stack>

        <Stack className={classes.rightHeader}>
          <ButtonField
            disabled={isViewOnly}
            startIcon={<CharacterPlusIcon />}
            style={{
              height: '32px',
              minWidth: '135px',
              borderRadius: '5px',
            }}
            name= {t('commonActionsNewValve')}
            onClick={handleAddValve}
          />
        </Stack>
      </Stack>

      <Collapse
        in={open}
        timeout="auto"
        unmountOnExit
        sx={{
          padding: '16px',
          border: '1px solid #D7D7D7',
          borderTop: 'unset',
        }}
      >
        {valveList && valveList?.length > 0 && (
          <FlowValveTable
            rows={valveList}
            onDeleteItem={handleDeleteItem}
            flowRuleId={flowRuleId}
          />
        )}

        {valveList && valveList.length === 0 && (
          <NoData text={ t('noData') } height={'200px'} />
        )}
      </Collapse>
    </Stack>
  )
}

export default FlowValveView
